// aa-as-seen-in.js

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    slowdownFactor: 0.75, // 0 < slowdownFactor ≤ 1 (lower = slower)
    breakpoint: 750,
    resizeDebounceMs: 250,
    imageLoadTimeoutMs: 5,
  };

  // Store active instances for cleanup
  const activeInstances = new Map();

  /**
   * Debounce function for resize events
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Check if user prefers reduced motion
   */
  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /**
   * Wait for images to load before calculating dimensions
   */
  function waitForImages(container) {
    return new Promise((resolve) => {
      const images = container.querySelectorAll("img");
      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedCount = 0;
      const totalImages = images.length;
      const timeout = setTimeout(() => resolve(), CONFIG.imageLoadTimeoutMs);

      function imageLoaded() {
        loadedCount++;
        if (loadedCount === totalImages) {
          clearTimeout(timeout);
          resolve();
        }
      }

      images.forEach((img) => {
        if (img.complete) {
          imageLoaded();
        } else {
          img.addEventListener("load", imageLoaded, { once: true });
          img.addEventListener("error", imageLoaded, { once: true });
        }
      });
    });
  }

  /**
   * Calculate total width of marquee content
   */
  function calculateTotalWidth(inner) {
    // First try scrollWidth
    let totalW = inner.scrollWidth;

    if (totalW === 0) {
      // Fallback: sum individual item widths
      const items = inner.querySelectorAll(".aa-marquee__item");
      totalW = Array.from(items).reduce((sum, item) => {
        const rect = item.getBoundingClientRect();
        const style = getComputedStyle(item);
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        return sum + rect.width + marginLeft + marginRight;
      }, 0);
    }

    return totalW;
  }

  /**
   * Apply marquee animation to a single row
   */
  function applyMarqueeAnimation(marquee, config) {
    const { row, isDesktop, reverse, showDesktop2, speed, idSuffix } = config;
    const inner = marquee.querySelector(".aa-marquee__inner");

    if (!inner) {
      console.warn(
        `[aa-as-seen-in][${idSuffix}] row ${row} missing .aa-marquee__inner`
      );
      return;
    }

    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) {
      inner.style.animation = "none";
      return;
    }

    const totalW = calculateTotalWidth(inner);
    if (totalW === 0) {
      console.warn(`[aa-as-seen-in][${idSuffix}] row ${row} has zero width`);
      return;
    }

    const loops =
      isDesktop && (row === 1 || (row === 2 && showDesktop2)) ? 3 : 2;
    const distance = totalW / loops;
    const duration = speed > 0 ? distance / speed : 0;
    // const delay = row === 2 ? -duration / 2 : 0;

    // Determine animation name
    let animName =
      row === 1 ? "scroll-left" : reverse ? "scroll-right" : "scroll-left";
    if (isDesktop && (row === 1 || (row === 2 && showDesktop2))) {
      animName += "-desktop";
    }

    // Apply animation
    Object.assign(inner.style, {
      animationName: animName,
      animationDuration: `${duration}s`,
      animationTimingFunction: "linear",
      animationIterationCount: "infinite",
      // animationDelay: `${delay}s`,
      animationPlayState: "running",
    });

    // Add hover pause functionality
    marquee.addEventListener("mouseenter", () => {
      inner.style.animationPlayState = "paused";
    });

    marquee.addEventListener("mouseleave", () => {
      if (!prefersReducedMotion()) {
        inner.style.animationPlayState = "running";
      }
    });
  }

  /**
   * Initialize marquee for a section
   */
  async function runForSection(sectionEl) {
    const idSuffix = sectionEl.id.replace("aa-as-seen-in-", "");

    try {
      // Get configuration from data attributes
      const mobileSpeed =
        Number(sectionEl.getAttribute("data-mobile-speed")) || 50;
      const desktopSpeed =
        Number(sectionEl.getAttribute("data-desktop-speed")) || 50;
      const reverse = sectionEl.getAttribute("data-reverse") === "true";
      const showDesktop2 =
        sectionEl.getAttribute("data-show-desktop-row2") === "true";
      const isDesktop = window.innerWidth >= CONFIG.breakpoint;
      const baseSpeed = isDesktop ? desktopSpeed : mobileSpeed;
      const speed = baseSpeed * CONFIG.slowdownFactor;

      // Wait for images to load
      await waitForImages(sectionEl);

      // Apply animations to each marquee row
      const marquees = sectionEl.querySelectorAll(".aa-marquee");
      marquees.forEach((marquee) => {
        const row = Number(marquee.dataset.row);
        applyMarqueeAnimation(marquee, {
          row,
          isDesktop,
          reverse,
          showDesktop2,
          speed,
          idSuffix,
        });
      });

      // Store instance for cleanup
      activeInstances.set(idSuffix, {
        sectionEl,
        initialized: true,
      });
    } catch (error) {
      console.error(
        `[aa-as-seen-in][${idSuffix}] initialization failed:`,
        error
      );
    }
  }

  /**
   * Handle window resize
   */
  const handleResize = debounce(() => {
    activeInstances.forEach((instance, idSuffix) => {
      if (instance.sectionEl && document.contains(instance.sectionEl)) {
        runForSection(instance.sectionEl);
      } else {
        // Clean up removed sections
        activeInstances.delete(idSuffix);
      }
    });
  }, CONFIG.resizeDebounceMs);

  /**
   * Initialize resize listener
   */
  function initResizeListener() {
    if (!window.asSeenInResizeListenerAdded) {
      window.addEventListener("resize", handleResize);
      window.asSeenInResizeListenerAdded = true;
    }
  }

  /**
   * Cleanup function for a specific section
   */
  function cleanup(idSuffix) {
    const instance = activeInstances.get(idSuffix);
    if (instance) {
      const marquees =
        instance.sectionEl.querySelectorAll(".aa-marquee__inner");
      marquees.forEach((inner) => {
        inner.style.animation = "none";
      });
      activeInstances.delete(idSuffix);
    }
  }

  /**
   * Exposed global functions
   */
  window.initAsSeenIn = function (idSuffix) {
    const sectionEl = document.getElementById(`aa-as-seen-in-${idSuffix}`);
    if (sectionEl) {
      // Initialize resize listener on first use
      initResizeListener();
      runForSection(sectionEl);
    } else {
      console.warn(`[aa-as-seen-in] no section found for ID "${idSuffix}"`);
    }
  };

  window.cleanupAsSeenIn = function (idSuffix) {
    cleanup(idSuffix);
  };

  // Listen for reduced motion preference changes
  if (window.matchMedia) {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    mediaQuery.addEventListener("change", () => {
      activeInstances.forEach((instance, idSuffix) => {
        if (instance.sectionEl && document.contains(instance.sectionEl)) {
          runForSection(instance.sectionEl);
        }
      });
    });
  }
})();
